import type { PracticeRecord } from "./types";
import { loadRecords, saveRecords } from "./store";
import { supabase, cloudConfigured } from "../cloud/supabase";
import { currentAuth } from "../auth/auth";
import { parseZoneId } from "./zoneId";

// Master practice dataset — Supabase table `practice_records` (every user's markings,
// with who marked them). Row-level security: users read/write their own rows, admins all.
// Every write goes through a durable outbox in localStorage first, so a marking is
// never lost while offline — it's sent in order and retried until it lands.

const TABLE = "practice_records";
const OUTBOX = "mytv.practice.outbox";

type Op =
  | { opId: string; kind: "upsert"; markedBy: string; record: PracticeRecord }
  | { opId: string; kind: "delete"; id: string; by: string };

export interface SyncStatus {
  state: "synced" | "syncing" | "offline";
  pending: number;
}

let status: SyncStatus = { state: "syncing", pending: 0 };
const listeners = new Set<(s: SyncStatus) => void>();

export function onSyncStatus(fn: (s: SyncStatus) => void): () => void {
  listeners.add(fn);
  fn(status);
  return () => listeners.delete(fn);
}
function emit(s: SyncStatus) {
  status = s;
  for (const fn of listeners) fn(s);
}

// ---------------------------------------------------------------- flat columns
const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null);
const int = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? Math.round(v) : null);
const str = (v: unknown) => (typeof v === "string" ? v : null);
const bool = (v: unknown) => (v == null ? null : v ? 1 : 0);

function flatten(rec: PracticeRecord, markedBy: string, userId: string, now: number) {
  const z = rec.zone ?? ({} as PracticeRecord["zone"]);
  const a = rec.auto ?? ({} as PracticeRecord["auto"]);
  const f = rec.final ?? ({} as PracticeRecord["final"]);
  const r = rec.result;
  const shownAt = int(rec.shownAt);
  const markedAt = int(rec.createdAt) ?? now;
  return {
    id: rec.id,
    user_id: userId,
    marked_by: markedBy,
    shown_at: shownAt,
    marked_at: markedAt,
    decide_ms: shownAt != null ? Math.max(0, markedAt - shownAt) : null,
    updated_at: now,
    edited_at: int(rec.editedAt),
    edited_by: str(rec.editedBy),
    engine: str(rec.engine),
    symbol: str(rec.symbol),
    detect_tf: str(rec.detectTf),
    detect_tf_sec: int(rec.detectTfSec),
    view_tf: str(rec.viewTf),
    signal_time: int(z.signalTime),
    zone_type: str(z.type),
    leg_outs: int(z.n),
    boring: int(z.b),
    star_w: bool(z.w),
    star_e: bool(z.e),
    side: str(z.side),
    proximal: num(z.proximal),
    distal: num(z.distal),
    atr: num(z.atr),
    auto_entry: num(a.entry),
    auto_stop: num(a.stop),
    auto_target: num(a.target),
    entry: num(f.entry),
    stop: num(f.stop),
    target: num(f.target),
    adjusted: bool(rec.adjusted),
    rr: num(rec.rrFinal),
    decision: str(rec.decision),
    original_decision: str(rec.originalDecision) ?? str(rec.decision),
    previewed: bool(rec.previewed) ?? 0,
    note: str(rec.note) ?? "",
    outcome: r ? str(r.outcome) : null,
    filled: r ? bool(r.filled) : null,
    r_multiple: r ? num(r.rMultiple) : null,
    pnl_points: r ? num(r.pnlPoints) : null,
    mfe_r: r ? num(r.mfeR) : null,
    mae_r: r ? num(r.maeR) : null,
    bars_to_fill: r ? int(r.barsToFill) : null,
    bars_in_trade: r ? int(r.barsInTrade) : null,
    fill_price: r ? num(r.fillPrice) : null,
    exit_price: r ? num(r.exitPrice) : null,
    record: { ...rec, markedBy },
  };
}

// ---------------------------------------------------------------- outbox
function loadOutbox(): Op[] {
  try {
    const a = JSON.parse(localStorage.getItem(OUTBOX) || "[]");
    return Array.isArray(a) ? a : [];
  } catch {
    return [];
  }
}
function saveOutbox(q: Op[]) {
  try {
    localStorage.setItem(OUTBOX, JSON.stringify(q));
  } catch {}
}
const opId = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

function enqueue(op: Op) {
  let q = loadOutbox();
  if (op.kind === "upsert") q = q.filter((o) => !(o.kind === "upsert" && o.record.id === op.record.id));
  q.push(op);
  saveOutbox(q);
}

export function pushUpsert(record: PracticeRecord): Promise<SyncStatus> {
  enqueue({ opId: opId(), kind: "upsert", markedBy: record.markedBy, record });
  return flush();
}

export function pushDelete(id: string, by: string): Promise<SyncStatus> {
  saveOutbox(loadOutbox().filter((o) => !(o.kind === "upsert" && o.record.id === id)));
  enqueue({ opId: opId(), kind: "delete", id, by });
  return flush();
}

const isNetworkError = (msg: string) => /fetch|network|timeout|Failed to/i.test(msg);

// true = done with this op (sent, or permanently rejected); false = retry later
async function send(op: Op): Promise<boolean> {
  const me = currentAuth();
  if (!cloudConfigured || !me) return false;
  const now = Date.now();
  const { error } =
    op.kind === "upsert"
      ? await supabase.from(TABLE).upsert(flatten(op.record, op.markedBy, me.id, now), { onConflict: "id" })
      : await supabase.from(TABLE).update({ deleted_at: now, deleted_by: op.by, updated_at: now }).eq("id", op.id).is("deleted_at", null);
  if (!error) return true;
  if (isNetworkError(error.message)) return false;
  console.warn("[practice] cloud rejected an op, dropping it", op, error.message);
  return true;
}

let running = false;
let again = false;

export async function flush(): Promise<SyncStatus> {
  if (running) {
    again = true;
    return status;
  }
  running = true;
  let offline = false;
  emit({ state: "syncing", pending: loadOutbox().length });
  try {
    do {
      again = false;
      for (;;) {
        const head = loadOutbox()[0];
        if (!head) break;
        if (!(await send(head))) {
          offline = true;
          break;
        }
        saveOutbox(loadOutbox().filter((o) => o.opId !== head.opId));
      }
    } while (again && !offline);
  } catch {
    offline = true;
  } finally {
    running = false;
  }
  const pending = loadOutbox().length;
  const s: SyncStatus = { state: pending === 0 ? "synced" : offline ? "offline" : "syncing", pending };
  emit(s);
  return s;
}

async function resetAtFor(username: string): Promise<number> {
  const { data } = await supabase.from("practice_meta").select("key, value").in("key", ["reset_at:*", `reset_at:${username}`]);
  return Math.max(0, ...(data ?? []).map((r) => Number(r.value)));
}

// Two-way sync for the signed-in user — the cloud is the source of truth:
//  1. send everything still queued, 2. pull their cloud records (admin edits win,
//  admin deletes remove the local copy, resets drop pre-reset copies),
//  3. upload local records the cloud has never seen. Returns true if local changed.
export async function reconcile(markedBy: string): Promise<boolean> {
  const st = await flush();
  const me = currentAuth();
  if (st.state === "offline" || !me || !cloudConfigured) return false;
  let changed = false;
  try {
    const rows: { id: string; deleted_at: number | null; record: PracticeRecord }[] = [];
    for (let from = 0; ; from += 1000) {
      const { data, error } = await supabase.from(TABLE).select("id, deleted_at, record").eq("user_id", me.id).range(from, from + 999);
      if (error) throw error;
      rows.push(...(data as typeof rows));
      if (!data || data.length < 1000) break;
    }
    const resetAt = await resetAtFor(markedBy);
    const cloud = new Map(rows.map((r) => [r.id, r]));
    const out: PracticeRecord[] = [];
    for (const rec of loadRecords()) {
      const c = cloud.get(rec.id);
      if (!c && rec.createdAt <= resetAt) changed = true; // wiped by a reset
      else if (!c) {
        enqueue({ opId: opId(), kind: "upsert", markedBy, record: { ...rec, markedBy: rec.markedBy || markedBy } });
        out.push(rec);
      } else if (c.deleted_at) changed = true;
      else {
        if (JSON.stringify(c.record) !== JSON.stringify({ ...rec, markedBy: rec.markedBy || markedBy })) changed = true;
        out.push(c.record);
      }
      cloud.delete(rec.id);
    }
    for (const c of cloud.values()) {
      if (!c.deleted_at) {
        out.push(c.record);
        changed = true;
      }
    }
    out.sort((a, b) => a.createdAt - b.createdAt);
    if (changed) saveRecords(out);
  } catch (e) {
    console.warn("[practice] reconcile failed", (e as Error).message);
  }
  await flush();
  return changed;
}

// ---------------------------------------------------------------- admin
export interface MasterRow {
  id: string;
  marked_by: string;
  shown_at: number | null;
  marked_at: number;
  decide_ms: number | null;
  updated_at: number;
  edited_at: number | null;
  edited_by: string | null;
  deleted_at: number | null;
  deleted_by: string | null;
  detect_tf: string;
  signal_time: number;
  zone_type: string;
  leg_outs: number;
  boring: number;
  side: string;
  entry: number;
  stop: number;
  target: number;
  rr: number;
  adjusted: number;
  decision: "approved" | "rejected";
  original_decision: string;
  previewed: number;
  note: string;
  outcome: string | null;
  filled: number | null;
  r_multiple: number | null;
}

export interface ListQuery {
  markedBy?: string;
  decision?: string;
  outcome?: string;
  q?: string;
  deleted?: "exclude" | "include" | "only";
  limit?: number;
  offset?: number;
}

const LIST_COLS =
  "id, marked_by, shown_at, marked_at, decide_ms, updated_at, edited_at, edited_by, deleted_at, deleted_by, detect_tf, signal_time, zone_type, leg_outs, boring, side, entry, stop, target, rr, adjusted, decision, original_decision, previewed, note, outcome, filled, r_multiple";

function must<T>(r: { data: T | null; error: { message: string } | null }): T {
  if (r.error) throw new Error(r.error.message);
  return r.data as T;
}

export async function listMaster(q: ListQuery): Promise<{ total: number; rows: MasterRow[] }> {
  let query = supabase.from(TABLE).select(LIST_COLS, { count: "exact" });
  const deleted = q.deleted ?? "exclude";
  if (deleted === "exclude") query = query.is("deleted_at", null);
  else if (deleted === "only") query = query.not("deleted_at", "is", null);
  if (q.markedBy) query = query.eq("marked_by", q.markedBy);
  if (q.decision) query = query.eq("decision", q.decision);
  if (q.outcome) query = query.eq("outcome", q.outcome);
  const zid = q.q ? parseZoneId(q.q) : null;
  if (zid) query = query.eq("zone_type", zid.type).eq("detect_tf", zid.tf).eq("signal_time", zid.signalTime);
  else if (q.q) {
    const s = q.q.replace(/[%,()]/g, " ").trim();
    if (s) query = query.or(`note.ilike.%${s}%,zone_type.ilike.%${s}%,detect_tf.ilike.%${s}%,marked_by.ilike.%${s}%`);
  }
  const limit = q.limit ?? 100;
  const offset = q.offset ?? 0;
  const r = await query.order("marked_at", { ascending: false }).range(offset, offset + limit - 1);
  if (r.error) throw new Error(r.error.message);
  return { total: r.count ?? 0, rows: (r.data ?? []) as unknown as MasterRow[] };
}

export async function editMaster(id: string, by: string, changes: { decision?: string; note?: string }) {
  const row = must(await supabase.from(TABLE).select("marked_by, user_id, record").eq("id", id).single()) as {
    marked_by: string;
    user_id: string;
    record: PracticeRecord;
  };
  const rec = row.record;
  let changed = false;
  if (changes.decision && changes.decision !== rec.decision) {
    rec.originalDecision = rec.originalDecision ?? rec.decision;
    rec.decision = changes.decision as PracticeRecord["decision"];
    changed = true;
  }
  if (changes.note !== undefined && (rec.note ?? "") !== changes.note.trim()) {
    rec.note = changes.note.trim();
    changed = true;
  }
  if (!changed) return { ok: true, changed };
  rec.editedAt = Date.now();
  rec.editedBy = by;
  const { id: _id, user_id, marked_by, marked_at, shown_at, decide_ms, ...cols } = flatten(rec, row.marked_by, row.user_id, Date.now());
  must(await supabase.from(TABLE).update(cols).eq("id", id));
  return { ok: true, changed };
}

export async function deleteMaster(id: string, by: string) {
  const now = Date.now();
  must(await supabase.from(TABLE).update({ deleted_at: now, deleted_by: by, updated_at: now }).eq("id", id));
  return { ok: true };
}
export async function restoreMaster(id: string, _by: string) {
  must(await supabase.from(TABLE).update({ deleted_at: null, deleted_by: null, updated_at: Date.now() }).eq("id", id));
  return { ok: true };
}
// Archives then deletes (all users, or one); the cloud keeps the archive as the backup.
export async function resetMaster(_by: string, markedBy?: string) {
  const res = must(await supabase.rpc("practice_reset", { scope: markedBy ?? null })) as { deleted: number; archived: number; scope: string };
  return { ok: true, deleted: res.deleted, scope: res.scope, backup: `cloud archive (${res.archived} rows in practice_records_archive)` };
}

// ---------------------------------------------------------------- activity
export interface DayActivity {
  count: number;
  first: number;
  last: number;
  activeMs: number;
}
export interface UserActivity {
  user: string;
  byDay: Record<string, DayActivity>;
  total: number;
  activeMs: number;
  activeDays: number;
  practicedToday: boolean;
  streak: number;
  lastMarkedAt: number | null;
}
export interface Activity {
  days: string[];
  today: string;
  users: UserActivity[];
}

const MAX_ACTIVE_PER_MARK = 10 * 60 * 1000;
const pad2 = (n: number) => String(n).padStart(2, "0");
export const localDayKey = (ms: number) => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

// Practice activity bucketed into the viewer's local calendar days.
export async function fetchActivity(days = 30, users: string[] = []): Promise<Activity> {
  const since = Date.now() - (days + 1) * 86400000;
  const rows: { marked_by: string; marked_at: number; decide_ms: number | null }[] = [];
  for (let from = 0; ; from += 1000) {
    const data = must(
      await supabase.from(TABLE).select("marked_by, marked_at, decide_ms").is("deleted_at", null).gte("marked_at", since).range(from, from + 999)
    ) as typeof rows;
    rows.push(...data);
    if (data.length < 1000) break;
  }
  const dayKeys: string[] = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) dayKeys.push(localDayKey(new Date(now.getFullYear(), now.getMonth(), now.getDate() - i).getTime()));
  const inWindow = new Set(dayKeys);
  const map = new Map<string, UserActivity>();
  const get = (u: string) => {
    let a = map.get(u);
    if (!a) map.set(u, (a = { user: u, byDay: {}, total: 0, activeMs: 0, activeDays: 0, practicedToday: false, streak: 0, lastMarkedAt: null }));
    return a;
  };
  for (const u of users) get(u);
  for (const r of rows) {
    const day = localDayKey(r.marked_at);
    if (!inWindow.has(day)) continue;
    const a = get(r.marked_by);
    const d = (a.byDay[day] ??= { count: 0, first: r.marked_at, last: r.marked_at, activeMs: 0 });
    const active = Math.min(r.decide_ms ?? 0, MAX_ACTIVE_PER_MARK);
    d.count++;
    d.first = Math.min(d.first, r.marked_at);
    d.last = Math.max(d.last, r.marked_at);
    d.activeMs += active;
    a.total++;
    a.activeMs += active;
    a.lastMarkedAt = Math.max(a.lastMarkedAt ?? 0, r.marked_at);
  }
  const today = dayKeys[dayKeys.length - 1];
  for (const a of map.values()) {
    a.activeDays = Object.keys(a.byDay).length;
    a.practicedToday = !!a.byDay[today];
    let i = dayKeys.length - 1;
    if (!a.practicedToday) i--;
    for (; i >= 0 && a.byDay[dayKeys[i]]; i--) a.streak++;
  }
  return { days: dayKeys, today, users: [...map.values()].sort((x, y) => y.total - x.total || x.user.localeCompare(y.user)) };
}

// ---------------------------------------------------------------- master export
async function allLive(): Promise<Record<string, unknown>[]> {
  const rows: Record<string, unknown>[] = [];
  for (let from = 0; ; from += 1000) {
    const data = must(await supabase.from(TABLE).select("*").is("deleted_at", null).order("marked_at").range(from, from + 999)) as Record<string, unknown>[];
    rows.push(...data);
    if (data.length < 1000) break;
  }
  return rows;
}
function download(name: string, mime: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: mime }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
const csvCell = (v: unknown) => {
  const s = v == null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
export async function exportMasterJSON() {
  const rows = await allLive();
  download("practice-master.json", "application/json", JSON.stringify(rows.map((r) => ({ ...(r.record as object), markedBy: r.marked_by, markedAt: r.marked_at, shownAt: r.shown_at })), null, 2));
}
export async function exportMasterCSV() {
  const rows = await allLive();
  const cols = rows.length ? Object.keys(rows[0]).filter((c) => c !== "record" && c !== "user_id") : [];
  download("practice-master.csv", "text/csv", [cols.join(","), ...rows.map((r) => cols.map((c) => csvCell(r[c])).join(","))].join("\n"));
}
